/* Product Finder client - powers Platform → Brand → Model flow via Storefront API
 * No external deps; uses fetch and progressive enhancement
 */

(() => {
  const sectionEl =
    document.currentScript?.closest('section.product-finder') || document.querySelector('section.product-finder');
  if (!sectionEl) return;

  const apiVersion = sectionEl.dataset.apiVersion;
  const token = sectionEl.dataset.storefrontToken;
  const shopDomain = sectionEl.dataset.shopDomain;

  const stepsEl = sectionEl.querySelector('.product-finder__steps');
  const progressBar = sectionEl.querySelector('.product-finder__progress-bar');
  const stepper = sectionEl.querySelector('[data-stepper]');

  const state = {
    platform: null,
    brand: null,
    model: null,
    brandList: [],
    modelList: [],
    productMatch: null,
  };

  const stepOrder = ['platform', 'brand', 'model', 'result'];

  function setProgress(step) {
    const idx = Math.max(0, stepOrder.indexOf(step));
    if (stepper) {
      const steps = Array.from(stepper.querySelectorAll('.pf-step'));
      steps.forEach((el, i) => el.classList.toggle('is-complete', i <= idx));
    }
  }

  function goto(step) {
    stepsEl.dataset.state = step;
    sectionEl.querySelectorAll('.product-finder__step').forEach((s) => {
      s.classList.toggle('is-hidden', s.dataset.step !== step);
    });
    setProgress(step);
    if (step === 'platform') initializeIcons();
  }

  // Helpers
  const gql = async (query, variables) => {
    const res = await fetch(`https://${shopDomain}/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new Error('Network error');
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e) => e.message).join(', '));
    return json.data;
  };

  // Fetch distinct brands for platform using product tags (brand-*)
  async function fetchBrandsForPlatform(platform) {
    // We fetch first N pages and de-duplicate tags client-side
    const pageSize = 100;
    let cursor = null;
    const brands = new Set();
    for (let i = 0; i < 5; i++) {
      // safeguard pages
      const data = await gql(
        `
        query($pageSize:Int!, $cursor:String, $platform:String!) {
          products(first: $pageSize, after: $cursor, query: $platform) {
            pageInfo { hasNextPage }
            edges { cursor node { tags } }
          }
        }
      `,
        { pageSize, cursor, platform: `tag:${platform}` }
      );
      const edges = data?.products?.edges || [];
      for (const { cursor: c, node } of edges) {
        cursor = c;
        for (const tag of node.tags) {
          if (tag.startsWith('brand-')) brands.add(tag.replace('brand-', ''));
        }
      }
      if (!data?.products?.pageInfo?.hasNextPage) break;
    }
    return Array.from(brands).sort((a, b) => a.localeCompare(b));
  }

  // Attempt to fetch a collection image for the given brand
  async function fetchBrandLogoUrl(brand) {
    const tryQueries = [`title:${brand}`, `handle:${slugify(brand)}`];
    for (const q of tryQueries) {
      try {
        const data = await gql(
          `
          query($q:String!) {
            collections(first: 1, query: $q) {
              edges { node { image { url altText } } }
            }
          }
        `,
          { q }
        );
        const edge = data?.collections?.edges?.[0];
        const url = edge?.node?.image?.url;
        if (url) return url;
      } catch (_) {
        /* ignore and continue */
      }
    }
    return null;
  }

  function slugify(text) {
    return String(text)
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function renderBrandSkeleton(brand) {
    const initials = brand
      .split(' ')
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || '')
      .join('');
    return `
      <button class="brand-card" data-brand="${brand}">
        <span class="brand-card__logo brand-card__logo--mono" aria-hidden="true">${initials}</span>
        <span class="brand-card__content">
          <span class="brand-card__title">${brand}</span>
          <span class="brand-card__meta">Select brand</span>
        </span>
      </button>
    `;
  }

  async function renderBrandCards(brands, container) {
    container.innerHTML = brands.map(renderBrandSkeleton).join('');
    // enhance with logos where available
    for (const brand of brands) {
      const url = await fetchBrandLogoUrl(brand);
      if (!url) continue;
      const card = container.querySelector(`[data-brand="${CSS.escape(brand)}"]`);
      if (!card) continue;
      const logo = document.createElement('img');
      logo.className = 'brand-card__logo pf-img-loading';
      logo.src = url;
      logo.alt = `${brand} logo`;
      logo.addEventListener('load', () => logo.classList.remove('pf-img-loading'));
      const mono = card.querySelector('.brand-card__logo--mono');
      mono?.replaceWith(logo);
    }
  }

  // Fetch models for brand within platform (model-*)
  async function fetchModelsForBrand(platform, brand) {
    const pageSize = 100;
    let cursor = null;
    const models = new Set();
    const queryStr = `tag:${platform} AND tag:brand-${brand}`;
    for (let i = 0; i < 5; i++) {
      const data = await gql(
        `
        query($pageSize:Int!, $cursor:String, $q:String!) {
          products(first: $pageSize, after: $cursor, query: $q) {
            pageInfo { hasNextPage }
            edges { cursor node { title tags } }
          }
        }
      `,
        { pageSize, cursor, q: queryStr }
      );
      const edges = data?.products?.edges || [];
      for (const { cursor: c, node } of edges) {
        cursor = c;
        for (const tag of node.tags) {
          if (tag.startsWith('model-')) models.add(tag.replace('model-', ''));
        }
      }
      if (!data?.products?.pageInfo?.hasNextPage) break;
    }
    return Array.from(models).sort((a, b) => a.localeCompare(b));
  }

  // Find matching product for platform+brand+model; prefer in-stock variant
  async function findProduct(platform, brand, model) {
    const q = [`tag:${platform}`];
    if (brand) q.push(`tag:brand-${brand}`);
    if (model) q.push(`tag:model-${model}`);
    const queryStr = q.join(' AND ');
    const data = await gql(
      `
      query($q:String!) {
        products(first: 10, query: $q) {
          edges {
            node {
              id handle title description tags featuredImage { url altText }
              variants(first: 50) {
                edges { node { id title availableForSale quantityAvailable selectedOptions { name value } price { amount currencyCode } }}
              }
            }
          }
        }
      }
    `,
      { q: queryStr }
    );
    const nodes = data?.products?.edges?.map((e) => e.node) || [];
    if (!nodes.length) return null;
    // Basic pick rule: any in-stock product first; otherwise first product
    const pick = nodes.find((n) => n.variants?.edges?.some((v) => v.node.availableForSale)) || nodes[0];
    return pick;
  }

  function renderCards(list, container, dataAttr, labelMap) {
    container.innerHTML = '';
    for (const item of list) {
      const b = document.createElement('button');
      b.className = 'pf-card';
      b.setAttribute(dataAttr, item);
      b.innerHTML = `<span class="pf-card__title">${labelMap?.(item) || item}</span>`;
      container.appendChild(b);
    }
  }

  function formatPriceMoney(price) {
    if (!price) return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: price.currencyCode }).format(
        Number(price.amount)
      );
    } catch (_) {
      return `$${Number(price.amount).toFixed(2)}`;
    }
  }

  function extractQualifiersFromTitle(title) {
    const quals = [];
    if (!title) return quals;
    const t = title;
    const gen = t.match(/Gen(?:eration)?\s*(\d+)/i);
    if (gen) quals.push(`Gen ${gen[1]}`);
    const cal = t.match(/(9mm|10mm|\.40s?&?w|\.45acp|\.45|\.357sig|\.38sup)/i);
    if (cal) quals.push(cal[1].toUpperCase().replace('ACp', 'ACP').replace('S&W', 'S&W'));
    const slide = t.match(/(\d(?:\.\d{1,2})?")/);
    if (slide) quals.push(slide[1]);
    return quals;
  }

  async function fetchPriceAvailabilityForModel(platform, brand, model) {
    const product = await findProduct(platform, brand, model);
    if (!product) return { model, product: null };
    // price: minimum across variants
    const prices = (product.variants?.edges || []).map((e) => e.node?.price).filter(Boolean);
    const min = prices.sort((a, b) => Number(a.amount) - Number(b.amount))[0];
    // availability
    const qty = (product.variants?.edges || [])
      .map((e) => (typeof e.node.quantityAvailable === 'number' ? e.node.quantityAvailable : 0))
      .reduce((a, b) => a + b, 0);
    const available = (product.variants?.edges || []).some((e) => e.node.availableForSale);
    let availability = 'Backordered';
    if (available && qty <= 10) availability = 'Low stock';
    if (available && qty > 10) availability = 'In stock';
    const quals = extractQualifiersFromTitle(product.title);
    return { model, product, minPrice: min, availability, qualifiers: quals };
  }

  async function renderModelCards(platform, brand, models, container) {
    container.innerHTML = '';
    // skeletons
    for (const m of models) {
      const skel = document.createElement('button');
      skel.className = 'pf-card pf-card--model';
      skel.setAttribute('data-model', m);
      skel.innerHTML = `<div class="pf-card__row"><span class="pf-card__title">${prettyModel(
        m
      )}</span><span class="pf-card__cta">View details</span></div><p class="pf-card__meta-line">Loading…</p>`;
      container.appendChild(skel);
    }
    // fetch details with limited concurrency
    const concurrency = 4;
    let idx = 0;
    async function worker() {
      while (idx < models.length) {
        const i = idx++;
        const m = models[i];
        try {
          const info = await fetchPriceAvailabilityForModel(platform, brand, m);
          const card = container.querySelector(`[data-model="${CSS.escape(m)}"]`);
          if (!card) continue;
          const priceStr = info.minPrice ? `From ${formatPriceMoney(info.minPrice)}` : '';
          const meta = [priceStr, info.availability].filter(Boolean).join(' • ');
          const chips = (info.qualifiers || []).map((q) => `<span class=\"pf-chip\">${q}</span>`).join('');
          const img = info.product?.featuredImage?.url || '';
          const alt = info.product?.featuredImage?.altText || info.product?.title || '';
          const handle = info.product?.handle;
          if (handle) card.setAttribute('data-handle', handle);
          card.innerHTML = `
            ${
              img
                ? `<img class=\"pf-card__img pf-img-loading\" src=\"${img}\" alt=\"${alt}\" width=\"72\" height=\"72\">`
                : ''
            }
            <div class=\"pf-card__body\">
              <div class=\"pf-card__row\">
                <span class=\"pf-card__title\">${prettyModel(m)}</span>
                <span class=\"pf-card__cta\">View details</span>
              </div>
              <p class=\"pf-card__meta-line\">${meta || ''}</p>
              ${chips ? `<div class=\"pf-chips\">${chips}</div>` : ''}
            </div>
          `;
          const imgEl = card.querySelector('.pf-card__img');
          if (imgEl) imgEl.addEventListener('load', () => imgEl.classList.remove('pf-img-loading'));
        } catch (_) {}
      }
    }
    const workers = Array.from({ length: concurrency }, () => worker());
    await Promise.all(workers);
  }

  // Bind platform selection
  sectionEl.querySelectorAll('[data-platform]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      try {
        state.platform = btn.dataset.platform;
        sectionEl.querySelectorAll('.pf-card--platform').forEach((el) => el.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        state.brand = null;
        state.model = null;
        state.productMatch = null;
        // If rifle (AR-15 platform) → go directly to product page
        if (state.platform === 'rifle') {
          try {
            const product = await findProduct(`platform-${state.platform}`, null, null);
            if (product?.handle) {
              window.location.href = `/products/${product.handle}?from=finder`;
              return;
            }
          } catch (_) {}
          // fallback to result step with message
          goto('result');
          const result = sectionEl.querySelector('[data-result]');
          result.innerHTML = '<p>Loading product…</p>';
          const product = await findProduct(`platform-${state.platform}`, null, null);
          if (product) renderResult(product);
          return;
        }
        // Otherwise, continue to brand step
        goto('brand');
        const brandsGrid = sectionEl.querySelector('[data-grid="brands"]');
        brandsGrid.innerHTML = '<div class="pf-card"><span class="pf-card__title">Loading…</span></div>';
        state.brandList = await fetchBrandsForPlatform(`platform-${state.platform}`);
        await renderBrandCards(state.brandList, brandsGrid);
      } catch (e) {
        console.error(e);
        alert('Sorry, we could not load brands. Please try again.');
        goto('platform');
      }
    });
  });

  // Back buttons
  sectionEl.querySelectorAll('.pf-back').forEach((btn) => {
    btn.addEventListener('click', () => goto(btn.dataset.back));
  });

  // Brand → Models
  sectionEl.addEventListener('click', async (evt) => {
    const target = evt.target.closest('[data-brand]');
    if (!target) return;
    try {
      state.brand = target.getAttribute('data-brand');
      sectionEl.querySelectorAll('[data-brand]').forEach((el) => el.classList.remove('is-selected'));
      target.classList.add('is-selected');
      goto('model');
      const modelsGrid = sectionEl.querySelector('[data-grid="models"]');
      modelsGrid.innerHTML = '<div class="pf-card"><span class="pf-card__title">Loading…</span></div>';
      state.modelList = await fetchModelsForBrand(`platform-${state.platform}`, state.brand);
      // AR-15 path may have no model tags → short-circuit
      if (!state.modelList.length && state.platform === 'rifle') {
        const product = await findProduct(`platform-${state.platform}`, state.brand, null);
        state.productMatch = product;
        renderResult(product);
        goto('result');
        return;
      }
      await renderModelCards(`platform-${state.platform}`, state.brand, state.modelList, modelsGrid);
    } catch (e) {
      console.error(e);
      alert('Sorry, we could not load models. Please try again.');
      goto('brand');
    }
  });

  // Model → navigate directly to PDP when handle is known
  sectionEl.addEventListener('click', async (evt) => {
    const target = evt.target.closest('[data-model]');
    if (!target) return;
    try {
      state.model = target.getAttribute('data-model');
      const handle = target.getAttribute('data-handle');
      if (handle) {
        window.location.href = `/products/${handle}?from=finder`;
        return;
      }
      const product = await findProduct(`platform-${state.platform}`, state.brand, state.model);
      if (product?.handle) {
        window.location.href = `/products/${product.handle}?from=finder`;
        return;
      }
      alert('Sorry, we could not find a matching product.');
    } catch (e) {
      console.error(e);
      // stay on model step
    }
  });

  function prettyModel(model) {
    // Convert slugs like glock-19 → Glock 19; p365-xl → P365 XL
    return model
      .split('-')
      .map((part) => (/^\d+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
      .join(' ')
      .replace('And', '&');
  }

  function renderResult(product) {
    const result = sectionEl.querySelector('[data-result]');
    if (!product) {
      result.innerHTML = '<p>No exact match found. Try a different selection.</p>';
      sectionEl.querySelector('[data-cta]').setAttribute('href', '/collections/all');
      return;
    }
    const img = product.featuredImage?.url || window.shopifyAssetUrl || '';
    const title = product.title;
    const handle = product.handle;
    const price = product.variants?.edges?.[0]?.node?.price;
    const priceStr = price
      ? new Intl.NumberFormat(undefined, { style: 'currency', currency: price.currencyCode }).format(price.amount)
      : '';
    result.innerHTML = `
      <img src="${img}" alt="${product.featuredImage?.altText || title}">
      <div>
        <p class="product-finder__result-title">${title}</p>
        <p class="product-finder__result-meta">${priceStr}</p>
        <p class="product-finder__result-meta">${(product.tags || [])
          .filter((t) => t.startsWith('brand-') || t.startsWith('model-'))
          .join(' • ')}</p>
      </div>
    `;
    const cta = sectionEl.querySelector('[data-cta]');
    const override = sectionEl.dataset.primaryLink;
    if (override) {
      cta.setAttribute('href', override);
    } else {
      cta.setAttribute('href', `/products/${handle}?from=finder`);
    }
    const labelOverride = sectionEl.dataset.primaryLabel;
    if (labelOverride) cta.textContent = labelOverride;
  }

  function initializeIcons() {
    try {
      // Lucide auto-replace
      if (window.lucide?.createIcons) {
        window.lucide.createIcons();
      }
    } catch (_) {}
  }

  // First render: hydrate icons for platform step
  initializeIcons();

  // Support deep links ?platform=&brand=&model=
  try {
    const params = new URLSearchParams(location.search);
    const platform = params.get('platform');
    const brand = params.get('brand');
    const model = params.get('model');
    if (platform && sectionEl.querySelector(`[data-platform="${platform}"]`)) {
      sectionEl.querySelector(`[data-platform="${platform}"]`).click();
      if (brand) {
        (async () => {
          // Wait for brands to load
          let tries = 0;
          while (!sectionEl.querySelector(`[data-brand="${brand}"]`) && tries < 40) {
            await new Promise((r) => setTimeout(r, 100));
            tries++;
          }
          const brandEl = sectionEl.querySelector(`[data-brand="${brand}"]`);
          brandEl && brandEl.click();
          if (model) {
            let tries2 = 0;
            while (!sectionEl.querySelector(`[data-model="${model}"]`) && tries2 < 40) {
              await new Promise((r) => setTimeout(r, 100));
              tries2++;
            }
            const modelEl = sectionEl.querySelector(`[data-model="${model}"]`);
            modelEl && modelEl.click();
          }
        })();
      }
    }
  } catch (_) {}
})();
